import imageCompression, { Options } from "browser-image-compression";
import { NUMBERS_ONLY_REGEX } from "./regex";
import { IAttachmentFile } from "../interfaces/corporateEvent";
import { EVENT_BID_STATUSES, iBidType } from "../types/eventTypes";
import FileIcon from "../assets/svg-fc/b2b/FileIcon";
import PDFIcon from "../assets/svg-fc/b2b/PDFIcon";
import VideoFileIcon from "../assets/svg-fc/b2b/VideoFileIcon";
import DOCIcon from "../assets/svg-fc/b2b/DOCIcon";
import CSVIcon from "../assets/svg-fc/b2b/CSVIcon";
import ZIPIcon from "../assets/svg-fc/b2b/ZIPIcon";
import { ALLOWED_IMAGE_FILE_TYPES } from "../constants/commonConstants";
import { ROUTE_PATHS } from "../routes";
import { getSearchTerms, matchesSearchTerms } from "./textSearch";

export function truncString(str: string, max: number, add: string) {
	add = add || "...";
	return typeof str === "string" && str.length > max ? str.substring(0, max) + add : str;
}

export function truncateString(str: string, max: number, sep: string) {
	max = max || 10;

	var len = str.length;
	if (len > max) {
		sep = sep || "...";

		var seplen = sep.length;
		if (seplen > max) {
			return str.substring(len - max);
		}
		var n = -0.5 * (max - len - seplen);

		var center = len / 2;

		var front = str.substring(0, center - n);
		var back = str.substring(len - center + n);

		return front + sep + back;
	}

	return str;
}

export function mustBeANumber(value: string | undefined) {
	if (!value) return true;
	return value.match(NUMBERS_ONLY_REGEX);
}

export const getFormattedAmountWithDecimals = (amount: number) => {
	let formattedStr = parseFloat(amount.toString()).toFixed(2).toString();

	if (formattedStr.indexOf(".") === -1) {
		formattedStr += ".00";
	}

	return formattedStr;
};

export function addCommasInAmount(amount: number | string) {
	return amount !== undefined || amount !== null
		? getFormattedAmountWithDecimals(Number(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
		: "";
}

export const getBidOrPaidAmount = (bid: iBidType) =>
	bid.status === EVENT_BID_STATUSES.ACCEPTED ? bid.total : bid.subTotal;

export const isSafari = () =>
	navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome");

export const capitalizeFirstLetter = (text: string) => {
	return text.charAt(0).toUpperCase() + text.slice(1);
};

export const isUserOnMobile = () => {
	return typeof window != "undefined" && window.screen?.width <= 786;
};

export const formatTollFreeNumberToLocalFormat = (phoneNumber: string) => {
	let formattedNumber = phoneNumber.trim();

	if (formattedNumber.startsWith("+971")) {
		formattedNumber = formattedNumber.slice(4);
		formattedNumber = formattedNumber.replace(/^(\d{3})(\d+)/, "$1 $2");
		return formattedNumber;
	}

	if (formattedNumber.startsWith("+1")) {
		formattedNumber = formattedNumber.slice(2);
		formattedNumber = formattedNumber.replace(/^(\d{3})(\d{3})(\d+)/, "$1 $2 $3");
		return formattedNumber;
	}

	return formattedNumber;
};

export const extractStringFromUrl = (value: string) => {
	return value.split("/")?.[1];
};

export const extractEventDetailPath = (url: string, pathName: string) => {
	let [baseUrl] = url.split("?");

	let parts = baseUrl.split("/");

	let index = parts.indexOf(pathName);

	if (index !== -1) {
		return true;
	}

	return false;
};

export const getStringsFirstLetter = (firstLetter: string = "", secondLetter: string = "") => {
	if (!firstLetter && !secondLetter) return "";
	const firstLetterFirstName = firstLetter.charAt(0);
	const firstLetterLastName = secondLetter?.charAt(0);
	return `${firstLetterFirstName}${firstLetterLastName || ""}`;
};

export const formatDate = (inputDate: string, sliceYear = true) => {
	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

	if (!inputDate) return "";

	const dateParts = inputDate.split("-");
	const year = dateParts[0];
	const month = parseInt(dateParts[1]) - 1;
	const day = parseInt(dateParts[2]);

	const formattedDate = `${day} ${months[month]} ${sliceYear ? `‘${year.slice(-2)}` : year}`;
	return formattedDate;
};

export function isObjectEmpty(obj: any) {
	return obj ? Object.keys(obj).length === 0 && obj.constructor === Object : true;
}

export const getFullName = (firstName = "", lastName = "") => {
	return `${firstName} ${lastName}`.trim();
};

export const filterBySearchString = <T extends Record<string, any>>(data: T[], search: string): T[] => {
	const terms = getSearchTerms(search);

	return data.filter((item) => matchesSearchTerms(terms, Object.values(item)));
};

export const getFileType = (attachment: IAttachmentFile) => {
	return attachment.fileName.split(".").pop();
};

export const checkIfImage = (attachment: IAttachmentFile) => {
	const fileType = getFileType(attachment);
	return fileType === "png" || fileType === "jpg" || fileType === "jpeg";
};

export const checkFileTypeForPhotoLibrary = (file: File) => {
	return ALLOWED_IMAGE_FILE_TYPES.includes(file.type);
};

export const getFileIcon = (attachment: IAttachmentFile) => {
	const fileType = getFileType(attachment);
	return fileType === "pdf"
		? PDFIcon
		: fileType === "mp4" ||
			  fileType === "mov" ||
			  fileType === "hevc" ||
			  fileType === "mkv" ||
			  fileType === "avi"
			? VideoFileIcon
			: fileType === "doc" || fileType === "docx"
				? DOCIcon
				: fileType === "csv"
					? CSVIcon
					: fileType === "zip"
						? ZIPIcon
						: FileIcon;
};

export const getPdfNameFromUrl = (url: string): string => {
	try {
		const fileName = url.split("/").pop();

		return fileName
			? decodeURIComponent(fileName)
					.replace(/\s*-\s*\d+\.pdf$/i, "")
					.replace(/\s*PDF$/i, "")
					.toLowerCase()
			: "";
	} catch {
		return "";
	}
};

/**
 * Compresses a single image file
 */
const compressSingleImage = async (file: File, onProgress?: (progress: number) => void): Promise<File> => {
	const compressedOptions: Options = {
		maxSizeMB: 1,
		useWebWorker: true,
		initialQuality: 0.5,
		alwaysKeepResolution: true,
		onProgress: onProgress,
	};

	try {
		const compressed = await imageCompression(file, compressedOptions);
		return new File([compressed], file.name, { type: file.type });
	} catch (error) {
		console.log("compressSingleImage error -> ", error);
		// Return original file if compression fails
		return file;
	}
};

/**
 * Compresses one or more image files
 * @param files - Single File or array of Files to compress
 * @returns Compressed File(s) in the same format as input, or original file(s) if compression fails
 */
export const compressImages = async (
	files: File[] | File,
	onProgress?: (progress: number) => void,
): Promise<File[] | File> => {
	const isArray = Array.isArray(files);
	const filesArray = isArray ? files : [files];

	const compressedFiles = await Promise.all(
		filesArray.map((file) =>
			compressSingleImage(
				file,
				onProgress ? (progress) => onProgress(progress * filesArray.length) : undefined,
			),
		),
	);

	return isArray ? compressedFiles : compressedFiles[0];
};

export const extractHiderHeaderPaths = (url: string) => {
	let [baseUrl] = url.split("?");
	const chatsPath = ROUTE_PATHS.CHATS;
	const supportChatPage = ROUTE_PATHS.ADMIN_VENDOR_CHAT;
	// Check if it's a chat detail page (has chatId in path)
	const isChatDetailPage = baseUrl.startsWith(chatsPath) && baseUrl.split("/").length > 2;
	const isAdminVendorChat = baseUrl.startsWith(supportChatPage) && baseUrl.split("/").length > 2;
	return isChatDetailPage || isAdminVendorChat;
};
